import React, { useState, useEffect } from 'react';
import './Carousel.css';

const Carousel = ({ items = [], onError }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [slides, setSlides] = useState([]);
    const [loadedIndexes, setLoadedIndexes] = useState(new Set());
    const [allImagesLoaded, setAllImagesLoaded] = useState(false);
    const [failedIndexes, setFailedIndexes] = useState(new Set());
    const [hasAnyError, setHasAnyError] = useState(false);

    const totalSlides = slides.length;

    useEffect(() => {
        const validSlides = items.filter(item => item?.Image || item?.PDF);
        setSlides(validSlides);
        setCurrentSlide(0);
        setLoadedIndexes(new Set());
        setFailedIndexes(new Set());
        setHasAnyError(false);
        setAllImagesLoaded(false);
    }, [items]);

    useEffect(() => {
        if (hasAnyError) {
            console.warn("Carousel disabled due to image load failure");
        }
    }, [hasAnyError]);


    useEffect(() => {
        if (totalSlides > 1) {
            const interval = setInterval(nextSlide, 5000);
            return () => clearInterval(interval);
        }
    }, [totalSlides]);


    const hasInvalidSlide = slides.some(
        item => !item?.Image && !item?.PDF
    );

    if (hasInvalidSlide) return null;
    if (hasAnyError) return null;
    if (totalSlides === 0) return null;

    
    const handleImageState = (index, isError = false) => {
        if (isError) {
            setFailedIndexes(prev => new Set(prev).add(index));
            setHasAnyError(true);
            if (onError) onError();
            return;
        }

        setLoadedIndexes(prev => {
            const newSet = new Set(prev);
            newSet.add(index);
            if (newSet.size === totalSlides && totalSlides > 0) {
                setAllImagesLoaded(true);
            }
            return newSet;
        });
    };

    const allFailed = failedIndexes.size === totalSlides && totalSlides > 0;

    const nextSlide = () => setCurrentSlide(prev => (prev === totalSlides - 1 ? 0 : prev + 1));
    const prevSlide = () => setCurrentSlide(prev => (prev === 0 ? totalSlides - 1 : prev - 1));

    const shouldLoadImage = (index) => {
        if (loadedIndexes.has(index)) return true;
        if (index === currentSlide) return true;
        if (index === (currentSlide + 1) % totalSlides) return true;
        if (index === (currentSlide - 1 + totalSlides) % totalSlides) return true;

        return false;
    };
    if (hasAnyError) return null;
    if (totalSlides === 0) return null;   

    return (
        <div className="carousel-container">
            <div className="carousel-wrapper">

                {(totalSlides - failedIndexes.size) > 1 && (
                    <>
                        <button className="carousel-nav carousel-prev" onClick={prevSlide}>
                            <svg viewBox="0 0 24 24" width="80" height="80" stroke="gray" fill="none" strokeWidth="0.5">
                                <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        <button className="carousel-nav carousel-next" onClick={nextSlide}>
                            <svg viewBox="0 0 24 24" width="80" height="80" stroke="gray" fill="none" strokeWidth="0.5">
                                <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </>
                )}

                <div className="carousel-track-container">
                    <div className="carousel-track">
                        {slides.map((item, index) => {
                            if (failedIndexes.has(index)) return null;

                            let position = (index - currentSlide + totalSlides) % totalSlides;
                            if (position > totalSlides / 2) position -= totalSlides;

                            return (
                                <div key={index} className={`carousel-slide position-${position} ${position === 0 ? 'active' : ''}`}>
                                    <div className="product-card-carousel">
                                        <div className="image-container">
                                            {allImagesLoaded || shouldLoadImage(index) ? (
                                                <img
                                                    src={item.Image || item.PDF}
                                                    alt={item.WEBDISPLAYNAME || 'Product'}
                                                    className="product-image-carousel"
                                                    loading="lazy"
                                                    decoding="async"
                                                    onLoad={() => handleImageState(index)}
                                                    onError={() => handleImageState(index, true)}
                                                />
                                            ) : (
                                                <div className="image-placeholder" />
                                            )}
                                        </div>
                                        {item.WEBDISPLAYNAME && (
                                            <div className="product-info-carousel">
                                                <h4 className="product-title">{item.WEBDISPLAYNAME}</h4>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Carousel;
